import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RadioGroupItem } from 'components';
import type {
  Immunization,
  ImmunizationDiagnosis,
  UpdateImmunizationPayload,
  VaccinationReason,
} from 'types/immunization';
import { INFLUENZA_ALERT_KEY } from 'types/preventionAlerts';
import {
  DiagnosisClassificationSelects,
  type DiagnosisClassificationField,
} from 'widgets/results/DiagnosisSection/DiagnosisClassificationSelects';
import { SEITE_SELECT_OPTIONS } from './diagnosis';
import {
  BatchInput,
  BlockCard,
  BlockContent,
  BlockHeader,
  BlockTitle,
  DiagnosisCheckbox,
  DiagnosisCode,
  DiagnosisDescription,
  DiagnosisRow,
  Divider,
  EntryBody,
  EntryCard,
  EntryContent,
  EntryHeader,
  EntryIcon,
  EntryIconImage,
  EntryItem,
  EntryList,
  EntryState,
  EntrySubtitle,
  EntryTexts,
  EntryTitle,
  EntryTrigger,
  Field,
  FieldLabel,
  GroupField,
  OptionLabel,
  OptionList,
  OptionRow,
  RemoveButton,
  RemoveRow,
} from './ImmunizationsSection.styles';

const DIAGNOSIS_FIELD_KEYS: Record<DiagnosisClassificationField, keyof ImmunizationDiagnosis> = {
  category: 'diagnosisCategory',
  sicherheit: 'diagnosisSicherheit',
  seite: 'diagnosisSeite',
};

/** Resolves false when the backend rejected the change, so the field can fall back. */
type UpdateHandler = (id: number, data: UpdateImmunizationPayload) => boolean | Promise<boolean>;

interface ImmunizationsSectionProps {
  immunizations: Immunization[];
  exportFailed?: boolean;
  isExporting?: boolean;
  onUpdate: UpdateHandler;
  onRemove: (id: number) => void;
  /** The dialog moves the focus here once it closes, because its trigger row is gone by then. */
  ref?: React.Ref<HTMLDivElement>;
}

const entryTitle = (immunization: Immunization): string =>
  immunization.batchNumber
    ? `${immunization.vaccineName} (${immunization.batchNumber})`
    : immunization.vaccineName;

interface ImmunizationEntryProps {
  immunization: Immunization;
  exportFailed: boolean;
  isExporting: boolean;
  onUpdate: UpdateHandler;
  onRemove: (id: number) => void;
}

const ImmunizationEntry = ({
  immunization,
  exportFailed,
  isExporting,
  onUpdate,
  onRemove,
}: ImmunizationEntryProps) => {
  const { t } = useTranslation();
  const idPrefix = useId();
  const [batchNumber, setBatchNumber] = useState(immunization.batchNumber);
  // Another window may have changed the batch; the field follows the stored value.
  useEffect(() => setBatchNumber(immunization.batchNumber), [immunization.batchNumber]);
  const locked = immunization.locked;
  const editable = !locked && !isExporting;

  const stateText = (() => {
    if (immunization.transferredAt) return t('result.immunization.states.transferred');
    if (!locked) return null;
    // While a retry runs, the steps already written are no reason to correct anything by hand.
    if (!exportFailed) return t('result.immunization.states.locked');
    if (immunization.transmittedSteps.length === 0) return t('result.immunization.states.failed');
    const steps = immunization.transmittedSteps
      .map((step) => t(`result.immunization.steps.${step}`))
      .join(', ');
    return `${t('result.immunization.states.partial')} · ${t('result.immunization.stepsDone', { steps })}`;
  })();

  const change = (data: UpdateImmunizationPayload) => {
    void onUpdate(immunization.id, data);
  };

  const commitBatch = () => {
    const next = batchNumber.trim();
    const stored = immunization.batchNumber;
    if (next === stored) return;
    void Promise.resolve(onUpdate(immunization.id, { batchNumber: next })).then((ok) => {
      if (!ok) setBatchNumber(stored);
    });
  };

  return (
    <EntryCard>
      <EntryItem value={String(immunization.id)}>
        <EntryTrigger>
          <EntryHeader>
            <EntryIcon>
              <EntryIconImage src={`/images/prevention-alerts/${INFLUENZA_ALERT_KEY}.svg`} alt="" />
            </EntryIcon>
            <EntryTexts>
              <EntryTitle>{entryTitle(immunization)}</EntryTitle>
              <EntrySubtitle>{t('result.immunization.subtitle')}</EntrySubtitle>
              {stateText && <EntryState>{stateText}</EntryState>}
            </EntryTexts>
          </EntryHeader>
        </EntryTrigger>
        <EntryContent>
          <EntryBody>
            <Field>
              <FieldLabel htmlFor={`${idPrefix}-batch`}>
                {t('result.immunization.batchLabel')}
              </FieldLabel>
              <BatchInput
                id={`${idPrefix}-batch`}
                value={batchNumber}
                maxLength={64}
                disabled={!editable}
                onChange={(event) => setBatchNumber(event.target.value)}
                onBlur={commitBatch}
              />
            </Field>
            {immunization.availableReasons.length > 0 && (
              <GroupField>
                <FieldLabel as="span" id={`${idPrefix}-reason-label`}>
                  {t('result.immunization.reasonLabel')}
                </FieldLabel>
                <OptionList
                  value={immunization.reason ?? ''}
                  disabled={!editable}
                  aria-labelledby={`${idPrefix}-reason-label`}
                  onValueChange={(value) => change({ reason: value as VaccinationReason })}
                >
                  {immunization.availableReasons.map((option) => {
                    const optionId = `${idPrefix}-reason-${option}`;
                    return (
                      <OptionRow key={option}>
                        <RadioGroupItem value={option} id={optionId} />
                        <OptionLabel htmlFor={optionId}>
                          {t(`result.immunization.reasons.${option}`)}
                        </OptionLabel>
                      </OptionRow>
                    );
                  })}
                </OptionList>
              </GroupField>
            )}
            <GroupField>
              <FieldLabel as="span">{t('result.immunization.diagnosesLabel')}</FieldLabel>
              <DiagnosisRow>
                <DiagnosisCheckbox
                  checked={immunization.diagnosisSelected}
                  disabled={!editable}
                  onCheckedChange={(checked) => change({ diagnosisSelected: checked === true })}
                  aria-label={t('result.immunization.diagnosisLabel')}
                />
                <DiagnosisCode>{immunization.diagnosisCode}</DiagnosisCode>
                <DiagnosisClassificationSelects
                  layout="compact"
                  category={immunization.diagnosisCategory}
                  sicherheit={immunization.diagnosisSicherheit}
                  seite={immunization.diagnosisSeite}
                  seiteOptions={SEITE_SELECT_OPTIONS}
                  disabled={!editable}
                  onChange={(field, value) => change({ [DIAGNOSIS_FIELD_KEYS[field]]: value })}
                />
              </DiagnosisRow>
              {immunization.diagnosisTitle && (
                <DiagnosisDescription>{immunization.diagnosisTitle}</DiagnosisDescription>
              )}
            </GroupField>
            {!locked && (
              <>
                <Divider />
                <RemoveRow>
                  <RemoveButton
                    type="button"
                    disabled={isExporting}
                    onClick={() => onRemove(immunization.id)}
                  >
                    {t('result.immunization.remove')}
                  </RemoveButton>
                </RemoveRow>
              </>
            )}
          </EntryBody>
        </EntryContent>
      </EntryItem>
    </EntryCard>
  );
};

export const ImmunizationsSection = ({
  immunizations,
  exportFailed = false,
  isExporting = false,
  onUpdate,
  onRemove,
  ref,
}: ImmunizationsSectionProps) => {
  const { t } = useTranslation();
  const title = t('result.immunization.blockTitle');

  if (immunizations.length === 0) return null;

  return (
    <BlockCard ref={ref} tabIndex={-1} role="region" aria-label={title} data-ph-no-autocapture>
      <BlockHeader>
        <BlockTitle>{title}</BlockTitle>
      </BlockHeader>
      <BlockContent>
        <EntryList type="single" collapsible>
          {immunizations.map((immunization) => (
            <ImmunizationEntry
              key={immunization.id}
              immunization={immunization}
              exportFailed={exportFailed}
              isExporting={isExporting}
              onUpdate={onUpdate}
              onRemove={onRemove}
            />
          ))}
        </EntryList>
      </BlockContent>
    </BlockCard>
  );
};
